import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Layout } from "@/components/Layout";
import { trackPageView, trackVisit } from "@/lib/analytics";

import Home from "@/pages/Home";
import Artists from "@/pages/Artists";
import ArtistProfile from "@/pages/ArtistProfile";
import RegisterMua from "@/pages/RegisterMua";
import ArtistRegister from "@/pages/ArtistRegister";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Wishlist from "@/pages/Wishlist";
import ComingSoon from "@/pages/ComingSoon";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsConditions from "@/pages/TermsConditions";
import RefundPolicy from "@/pages/RefundPolicy";

import AdminLogin from "@/admin/AdminLogin";
import AdminLayout from "@/admin/AdminLayout";
import AdminArtists from "@/admin/AdminArtists";
import AdminArtistEdit from "@/admin/AdminArtistEdit";
import AdminSiteImages from "@/admin/AdminSiteImages";
import AdminHomeSettings from "@/admin/AdminHomeSettings";
import AdminAnalytics from "@/admin/AdminAnalytics";
import AdminApplications from "@/admin/AdminApplications";
import AdminApplicationDetail from "@/admin/AdminApplicationDetail";
import AdminArtistForm from "@/admin/AdminArtistForm";
import AdminLocations from "@/admin/AdminLocations";
import AdminAnnouncements from "@/admin/AdminAnnouncements";

const PublicShell = ({ children }) => <Layout>{children}</Layout>;

// Fires a GA4 page_view event on every route change (admin/* routes excluded — internal use).
const RouteTracker = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (pathname.startsWith('/admin')) return;
    trackPageView(`${pathname}${search}`);
    trackVisit(`${pathname}${search}`);
  }, [pathname, search]);
  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <RouteTracker />
        <Routes>
          {/* Admin */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminArtists />} />
            <Route path="applications" element={<AdminApplications />} />
            <Route path="applications/:id" element={<AdminApplicationDetail />} />
            <Route path="artist-form" element={<AdminArtistForm />} />
            <Route path="locations" element={<AdminLocations />} />
            <Route path="announcements" element={<AdminAnnouncements />} />
            <Route path="artists/new" element={<AdminArtistEdit mode="create" />} />
            <Route path="artists/:slug/edit" element={<AdminArtistEdit mode="edit" />} />
            <Route path="site-images" element={<AdminSiteImages />} />
            <Route path="home-settings" element={<AdminHomeSettings />} />
            <Route path="analytics" element={<AdminAnalytics />} />
          </Route>

          {/* Public site */}
          <Route path="/" element={<PublicShell><Home /></PublicShell>} />
          <Route path="/artists" element={<PublicShell><Artists /></PublicShell>} />
          <Route path="/artists/:slug" element={<PublicShell><ArtistProfile /></PublicShell>} />
          <Route path="/register-mua" element={<PublicShell><RegisterMua /></PublicShell>} />
          <Route path="/register" element={<PublicShell><ArtistRegister /></PublicShell>} />
          <Route path="/about" element={<PublicShell><About /></PublicShell>} />
          <Route path="/contact" element={<PublicShell><Contact /></PublicShell>} />
          <Route path="/wishlist" element={<PublicShell><Wishlist /></PublicShell>} />
          <Route path="/privacy-policy" element={<PublicShell><PrivacyPolicy /></PublicShell>} />
          <Route path="/terms-conditions" element={<PublicShell><TermsConditions /></PublicShell>} />
          <Route path="/refund-policy" element={<PublicShell><RefundPolicy /></PublicShell>} />
          <Route path="/careers" element={<PublicShell><ComingSoon imageKey="careers_main" title="Careers" eyebrow="Hiring Soon" subtitle="We're building a team obsessed with Indian bridal beauty. Drop us a line at contact@findyourmua.com to start a conversation." /></PublicShell>} />
          <Route path="/blog" element={<PublicShell><ComingSoon imageKey="journal_main" title="The Journal" eyebrow="Editorial · Coming Soon" subtitle="Bridal beauty stories, artist interviews, and behind-the-scenes from India's most beautiful weddings." /></PublicShell>} />
          <Route path="*" element={<PublicShell><ComingSoon title="Page Not Found" eyebrow="404" subtitle="The page you're looking for doesn't exist. But there's plenty more beauty to discover." /></PublicShell>} />
        </Routes>
        <Toaster
          position="bottom-center"
          richColors
          toastOptions={{
            style: { borderRadius: 0, fontFamily: 'Outfit, sans-serif', fontSize: '14px' },
          }}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
